import React, { Component } from 'react';
import {
  Button, Dialog, DialogTitle, DialogContent, DialogActions,
  CircularProgress, Alert, Chip, IconButton, Tooltip
} from '@mui/material';
import { CheckCircle, Error as ErrorIcon, HourglassEmpty, SkipNext, ContentCopy, Refresh } from '@mui/icons-material';
import { connect } from 'react-redux';

import { getOnboardingStatus, resumeOnboarding } from '../../actions/onboarding';

const SMS_STEPS = [
  { key: 'stepCaptcha', label: 'Captcha' },
  { key: 'stepEmailCheck', label: 'DB Validation' },
  { key: 'stepDbTenant', label: 'Database Tenant' },
  { key: 'stepZitadelOrg', label: 'Create Organization' },
  { key: 'stepZitadelProjects', label: 'Setup Projects & Roles' },
  { key: 'stepZitadelUser', label: 'Create User' },
  { key: 'stepSmsConfig', label: 'SMS Configuration' },
  { key: 'stepBilling', label: 'Billing Setup' },
];

function StepIcon({ status }) {
  if (status === 'completed') return <CheckCircle sx={{ color: 'green', fontSize: 20 }} />;
  if (status === 'failed') return <ErrorIcon sx={{ color: 'red', fontSize: 20 }} />;
  if (status === 'skipped') return <SkipNext sx={{ color: 'grey', fontSize: 20 }} />;
  return <HourglassEmpty sx={{ color: 'grey', fontSize: 20 }} />;
}

class OnboardingStatus extends Component {
  constructor() {
    super();
    this.state = { resuming: false, copied: false };
    this.statusReq = false;
    this.resumeReq = false;
  }

  componentDidMount() {
    if (this.props.onboardingId) {
      this.statusReq = true;
      this.props.getOnboardingStatus(this.props.onboardingId);
    }
  }

  componentWillReceiveProps(nextProps) {
    if (this.resumeReq && nextProps.resumeResult) {
      this.resumeReq = false;
      this.setState({ resuming: false });
      this.statusReq = true;
      this.props.getOnboardingStatus(this.props.onboardingId);
    }
  }

  handleResume = () => {
    this.resumeReq = true;
    this.setState({ resuming: true });
    this.props.resumeOnboarding(this.props.onboardingId);
  }

  handleRefresh = () => {
    this.statusReq = true;
    this.props.getOnboardingStatus(this.props.onboardingId);
  }

  copyCredentials = () => {
    const ob = this.props.onboardingStatus;
    if (!ob) return;
    const text = `Username: ${ob.contactEmail}\nPassword: ${ob.tempPassword}`;
    navigator.clipboard.writeText(text).then(() => {
      this.setState({ copied: true });
      setTimeout(() => this.setState({ copied: false }), 2000);
    });
  }

  render() {
    const { onboardingStatus, onClose } = this.props;
    const { resuming, copied } = this.state;
    const ob = onboardingStatus;

    return (
      <Dialog open={true} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          Onboarding Status
          <Tooltip title="Refresh">
            <IconButton onClick={this.handleRefresh} size="small"><Refresh /></IconButton>
          </Tooltip>
        </DialogTitle>
        <DialogContent dividers>
          {!ob ? (
            <div style={{ textAlign: 'center', padding: 20 }}><CircularProgress /></div>
          ) : (
            <div>
              <div style={{ marginBottom: 16 }}>
                <p><strong>Tenant:</strong> {ob.tenantName}</p>
                <p><strong>Email:</strong> {ob.contactEmail}</p>
                <p><strong>Type:</strong> {ob.tenantType}</p>
                <p>
                  <strong>Status:</strong>{' '}
                  <Chip
                    label={ob.overallStatus}
                    size="small"
                    color={ob.overallStatus === 'completed' ? 'success' : ob.overallStatus === 'failed' ? 'error' : 'warning'}
                  />
                </p>
                {ob.zitadelOrgId && <p><strong>Org ID:</strong> {ob.zitadelOrgId}</p>}
                {ob.retryCount > 0 && <p><strong>Retry Count:</strong> {ob.retryCount}</p>}
              </div>

              <p style={{ fontWeight: 500, marginBottom: 8 }}>Steps:</p>
              {SMS_STEPS.map(step => {
                const steps = ob.steps || ob;
                const stepStatus = steps[step.key] || 'pending';
                return (
                  <div key={step.key} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 0' }}>
                    <StepIcon status={stepStatus} />
                    <span style={{ flex: 1 }}>{step.label}</span>
                    <Chip
                      label={stepStatus}
                      size="small"
                      variant="outlined"
                      color={stepStatus === 'completed' ? 'success' : stepStatus === 'failed' ? 'error' : 'default'}
                    />
                  </div>
                );
              })}

              {ob.failureReason && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  <strong>{ob.failureStep}:</strong> {ob.failureReason}
                </Alert>
              )}

              {ob.overallStatus === 'completed' && ob.tempPassword && (
                <div style={{ background: '#f5f5f5', padding: 12, borderRadius: 4, marginTop: 16 }}>
                  <p style={{ margin: 0 }}><strong>Username:</strong> {ob.contactEmail}</p>
                  <p style={{ margin: '4px 0 0' }}><strong>Temp Password:</strong> {ob.tempPassword}</p>
                  <Tooltip title={copied ? 'Copied!' : 'Copy credentials'}>
                    <IconButton onClick={this.copyCredentials} size="small" sx={{ mt: 1 }}>
                      <ContentCopy fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </div>
              )}
            </div>
          )}
        </DialogContent>
        <DialogActions>
          {ob && (ob.overallStatus === 'failed' || ob.overallStatus === 'in_progress') && (
            <Button onClick={this.handleResume} variant="contained" color="warning" disabled={resuming}>
              {resuming ? <CircularProgress size={18} sx={{ mr: 1 }} /> : null}
              Resume
            </Button>
          )}
          <Button onClick={onClose}>Close</Button>
        </DialogActions>
      </Dialog>
    );
  }
}

function mapStateToProps(state) {
  return {
    onboardingStatus: state.onboarding.onboardingStatus,
    resumeResult: state.onboarding.resumeResult,
  };
}

export default connect(mapStateToProps, { getOnboardingStatus, resumeOnboarding })(OnboardingStatus);
